// ============================================================================
// Resolución de derechos: bandera global ∧ licencia ∧ rol del tenant ∧ rol de
// plataforma — issues #643 y #804.
// ============================================================================
// Función PURA: no lee Firestore ni variables de entorno. El backend (#648) le
// pasa lo que leyó y el frontend sólo muestra el resultado.
//
// El backend tiene una copia en CommonJS (`functions/src/modules/entitlements/
// resolve.js`, #644). Ambas corren el mismo fixture
// `__fixtures__/resolution-cases.json`: si divergen, fallan las pruebas.
//
// Cómo se leen las listas de una licencia o un rol (igual que un árbol de
// casillas):
//  • Nombrar un nodo lo otorga completo, con todo lo que cuelga de él.
//  • Nombrar sólo algunos hijos otorga el padre "a medias": el padre aparece,
//    pero sólo con esos hijos.
//  • `overrides.remove` quita un nodo y lo que cuelga de él, aunque la licencia
//    lo incluya.
//  • Un nodo con `impliedBy` (#1346) se otorga también cuando el nivel nombra
//    cualquiera de esas llaves: así una sección nueva no deja fuera a los roles
//    y licencias guardados antes de que existiera.
//
// Cada plataforma resuelve sobre SU vista del catálogo común (catálogo v24,
// `tenantDefaults.ts`): `catalogFor` quita lo ajeno (llegó del otro fork sin
// código aquí) y aplica su status; una bandera que nadie escribió vale lo que
// fije la plataforma. Así cada una ve exactamente lo que veía con su catálogo
// propio.
// ============================================================================

import type {
  EntitlementKey,
  EntitlementNode,
  EntitlementStatus,
  FlagState,
  Grants,
  Reason,
  ResolveInput,
  ResolveResult,
  TenantTables,
} from "./types";
import { TENANT_TABLES } from "./tenantDefaults";

/** De menos a más restrictivo: con plataforma desconocida gana el último. */
const FLAG_STRICTNESS: readonly FlagState[] = ["on", "internal", "off"];
const STATUS_STRICTNESS: readonly EntitlementStatus[] = ["ga", "beta", "coming-soon", "deprecated"];

const own = (tables: TenantTables, tenant: string | null | undefined) =>
  tenant && Object.prototype.hasOwnProperty.call(tables, tenant) ? tables[tenant] : null;

const stricter = <T>(order: readonly T[], a: T | undefined, b: T | undefined): T | undefined =>
  a === undefined ? b : b === undefined ? a : order.indexOf(b) > order.indexOf(a) ? b : a;

/**
 * ¿La llave es AJENA a la plataforma (llegó del otro fork sin código aquí)? Para
 * esa plataforma no existe. Con plataforma desconocida, lo es si lo es en alguna.
 */
export function isForeign(
  key: EntitlementKey,
  tenant: string | null | undefined,
  tables: TenantTables = TENANT_TABLES,
): boolean {
  const table = own(tables, tenant);
  if (table) return table.foreign.includes(key);
  return Object.values(tables).some((t) => t.foreign.includes(key));
}

/**
 * Status del nodo EN la plataforma: `deprecated` si es ajeno; si no, el que fije
 * la plataforma; si no, el del catálogo común. Con plataforma desconocida, el
 * más retirado de todos.
 */
export function statusIn(
  node: EntitlementNode,
  tenant: string | null | undefined,
  tables: TenantTables = TENANT_TABLES,
): EntitlementStatus {
  if (isForeign(node.key, tenant, tables)) return "deprecated";
  const table = own(tables, tenant);
  if (table) return table.status[node.key] ?? node.status;
  let status: EntitlementStatus | undefined = node.status;
  for (const t of Object.values(tables)) status = stricter(STATUS_STRICTNESS, status, t.status[node.key]);
  return status ?? node.status;
}

/** ¿Está disponible en la plataforma (ni ajeno ni retirado ahí)? */
export function isAvailable(
  node: EntitlementNode,
  tenant: string | null | undefined,
  tables: TenantTables = TENANT_TABLES,
): boolean {
  return statusIn(node, tenant, tables) !== "deprecated";
}

/**
 * La vista del catálogo de una plataforma: sin lo ajeno y con SU status, sus
 * etiquetas y su padre donde difieren del común. Es lo que esa plataforma tenía
 * como catálogo propio; todo lo que lee el catálogo (editores, banderas, rutas,
 * licencias) trabaja sobre esta vista. Con plataforma desconocida no se aplican
 * etiquetas ni padres propios (sólo lo restrictivo).
 */
export function catalogFor(
  catalog: readonly EntitlementNode[],
  tenant: string | null | undefined,
  tables: TenantTables = TENANT_TABLES,
): EntitlementNode[] {
  const table = own(tables, tenant);
  const out: EntitlementNode[] = [];
  for (const node of catalog) {
    if (isForeign(node.key, tenant, tables)) continue;
    const status = statusIn(node, tenant, tables);
    const labels = table?.labels?.[node.key];
    const parent = table?.parent?.[node.key];
    // `replacedBy` sólo vale donde el nodo está retirado (vivo aquí, no se reemplaza).
    const keepsReplacement = status === "deprecated" || !node.replacedBy;
    if (status === node.status && keepsReplacement && !labels && !parent) {
      out.push(node);
      continue;
    }
    const { replacedBy, ...rest } = node;
    const view: EntitlementNode = {
      ...rest,
      ...(parent ? { parent } : {}),
      ...(labels ? { labels } : {}),
      status,
    };
    out.push(status === "deprecated" && replacedBy ? { ...view, replacedBy } : view);
  }
  return out;
}

/**
 * Estado de un nodo en una plataforma cuando nadie ha escrito su bandera global:
 * retirado ahí (o ajeno) → `off`; si no, lo que fije la plataforma
 * (`tenantDefaults.ts`); si no, lo que se deriva de su status ahí.
 */
export function defaultFlagState(
  node: EntitlementNode,
  tenant: string | null | undefined,
  tables: TenantTables = TENANT_TABLES,
): FlagState {
  const status = statusIn(node, tenant, tables);
  if (status === "deprecated") return "off";
  const fromStatus: FlagState = status === "coming-soon" ? "off" : "on";
  const table = own(tables, tenant);
  if (table) return table.defaults[node.key] ?? fromStatus;
  // Plataforma desconocida: lo más cerrado entre lo que da el status y lo que fije cualquiera.
  let strictest: FlagState = fromStatus;
  for (const t of Object.values(tables)) strictest = stricter(FLAG_STRICTNESS, strictest, t.defaults[node.key])!;
  return strictest;
}

interface Tree {
  byKey: Map<EntitlementKey, EntitlementNode>;
  children: Map<EntitlementKey, EntitlementKey[]>;
}

function buildTree(catalog: readonly EntitlementNode[]): Tree {
  const byKey = new Map<EntitlementKey, EntitlementNode>();
  const children = new Map<EntitlementKey, EntitlementKey[]>();
  for (const node of catalog) byKey.set(node.key, node);
  for (const node of catalog) {
    if (!node.parent) continue;
    const list = children.get(node.parent) ?? [];
    list.push(node.key);
    children.set(node.parent, list);
  }
  return { byKey, children };
}

function ancestors(tree: Tree, key: EntitlementKey): EntitlementKey[] {
  const out: EntitlementKey[] = [];
  let parent = tree.byKey.get(key)?.parent ?? null;
  while (parent) {
    out.push(parent);
    parent = tree.byKey.get(parent)?.parent ?? null;
  }
  return out;
}

interface Level {
  /** Otorgado completo (nombrado, o bajo algo nombrado). */
  full: Set<EntitlementKey>;
  /** Otorgado completo o "a medias" (ancestro de algo otorgado completo). */
  granted: Set<EntitlementKey>;
}

/**
 * Lo que otorga un nivel, ya expandido: nodos nombrados (o bajo uno nombrado) y
 * sus ancestros, menos lo quitado.
 */
function expand(
  tree: Tree,
  grants: Grants,
  removed: ReadonlySet<EntitlementKey>,
): Level {
  const listed = grants === "all" ? null : new Set(grants);
  const named = (key: EntitlementKey) =>
    listed === null || listed.has(key) || ancestors(tree, key).some((a) => listed.has(a));
  // `impliedBy` (#1346): nombrar una de esas llaves trae también este nodo. Un
  // solo salto, a propósito: lo implicado no implica a su vez.
  const isListed = (key: EntitlementKey) =>
    named(key) || (tree.byKey.get(key)?.impliedBy ?? []).some((k) => tree.byKey.has(k) && named(k));
  const isRemoved = (key: EntitlementKey) =>
    removed.has(key) || ancestors(tree, key).some((a) => removed.has(a));

  const full = new Set<EntitlementKey>();
  const granted = new Set<EntitlementKey>();
  for (const key of tree.byKey.keys()) {
    if (!isListed(key) || isRemoved(key)) continue;
    full.add(key);
    granted.add(key);
    for (const a of ancestors(tree, key)) granted.add(a);
  }
  return { full, granted };
}

/**
 * Lo que otorgan DOS niveles a la vez: completo sólo lo que ambos otorgan
 * completo, y "a medias" los ancestros de eso. Así, si un nivel da la app A y
 * el otro la B de la misma categoría, la categoría no se cuela.
 */
function intersect(tree: Tree, a: Level, b: Level): Level {
  const full = new Set<EntitlementKey>();
  const granted = new Set<EntitlementKey>();
  for (const key of a.full) {
    if (!b.full.has(key)) continue;
    full.add(key);
    granted.add(key);
    for (const anc of ancestors(tree, key)) granted.add(anc);
  }
  return { full, granted };
}

/**
 * Motivo de un nodo que se quedó sin hijos visibles. Se elige el que más le
 * sirve al usuario: si algo falta por su rol o por su plan, eso; si todo cayó
 * por la plataforma, "sólo interno" o "apagado".
 */
function emptyContainerReason(childReasons: Reason[]): Reason {
  if (childReasons.includes("not-in-role")) return "not-in-role";
  if (childReasons.includes("not-in-license")) return "not-in-license";
  // Un plan que SÓLO nombra cosas retiradas no trae nada: "no está en tu plan".
  if (childReasons.length > 0 && childReasons.every((r) => r === "deprecated")) {
    return "not-in-license";
  }
  if (childReasons.includes("flag-internal")) return "flag-internal";
  return "flag-off";
}

export function resolveEntitlements(input: ResolveInput): ResolveResult {
  // La vista de la plataforma (v24): lo ajeno no existe aquí y cada nodo trae su
  // status de aquí, así que motivos, poda y llaves desconocidas son los de antes.
  const tables = input.tenantTables ?? TENANT_TABLES;
  const catalog = catalogFor(input.catalog, input.tenant, tables);
  const tree = buildTree(catalog);
  const flags = input.flags ?? {};
  const admin = input.isPlatformAdmin === true;

  const unknown = new Set<EntitlementKey>();
  const note = (keys: readonly EntitlementKey[] | undefined) => {
    for (const k of keys ?? []) if (!tree.byKey.has(k)) unknown.add(k);
  };
  if (input.license !== "all") note(input.license);
  if (input.role !== "all") note(input.role);
  const platformGrants = input.platformRole?.grants ?? "all";
  if (platformGrants !== "all") note(platformGrants);
  note(input.platformRole?.denies);
  note(input.overrides?.add);
  note(input.overrides?.remove);

  const licenseList: Grants =
    admin || input.license === "all"
      ? "all"
      : [...input.license, ...(input.overrides?.add ?? [])];
  const removed = new Set(admin ? [] : (input.overrides?.remove ?? []));

  const inLicense = expand(tree, licenseList, removed);
  const inTenantRole = expand(tree, admin ? "all" : input.role, new Set());
  // Nivel 4 (#804): el rol de plataforma. Se cruza con el del tenant en un solo
  // nivel "rol": para el usuario las dos cosas son "tu rol no lo incluye", y
  // cruzarlas aquí hace que la poda y los motivos las traten igual.
  const inPlatformRole = expand(
    tree,
    admin ? "all" : platformGrants,
    new Set(admin ? [] : (input.platformRole?.denies ?? [])),
  );
  const inRole = intersect(tree, inTenantRole, inPlatformRole);

  // Motivos que vienen de la plataforma (no de la licencia ni del rol): si un
  // ancestro cae por uno de estos, el hijo cae con "parent-denied". Si el padre
  // sólo falta en la licencia o el rol, el hijo dice lo mismo por sí mismo, que
  // es lo que la interfaz necesita mostrar ("no está en tu plan").
  const GLOBAL: ReadonlySet<Reason> = new Set<Reason>([
    "deprecated",
    "flag-off",
    "flag-internal",
    "parent-denied",
  ]);

  const reasons: Record<EntitlementKey, Reason> = {};
  const reasonOf = (key: EntitlementKey): Reason => {
    const cached = reasons[key];
    if (cached) return cached;
    const node = tree.byKey.get(key)!;

    let reason: Reason;
    const flag = flags[key] ?? defaultFlagState(node, input.tenant, tables);
    if (node.status === "deprecated") reason = "deprecated";
    else if (flag === "off") reason = "flag-off";
    else if (flag === "internal" && !admin) reason = "flag-internal";
    else if (node.parent && tree.byKey.has(node.parent) && GLOBAL.has(reasonOf(node.parent)))
      reason = "parent-denied";
    else if (!inLicense.granted.has(key)) reason = "not-in-license";
    else if (!inRole.granted.has(key)) reason = "not-in-role";
    else reason = "granted";

    reasons[key] = reason;
    return reason;
  };

  for (const node of catalog) reasonOf(node.key);

  // Un nodo otorgado sólo "a medias" existe por sus hijos: si ninguno sobrevivió
  // (p. ej. la única app nombrada tiene la bandera en off), tampoco él. Se
  // recorre de lo más profundo a la raíz para que la poda suba en cascada.
  const depth = (key: EntitlementKey) => ancestors(tree, key).length;
  const deepestFirst = [...catalog].sort((a, b) => depth(b.key) - depth(a.key));
  for (const node of deepestFirst) {
    if (reasons[node.key] !== "granted") continue;
    const kids = tree.children.get(node.key) ?? [];
    if (kids.some((k) => reasons[k] === "granted")) continue;

    // Una categoría es sólo un contenedor: sin apps vivas no se muestra, aunque
    // se haya otorgado completa (p. ej. todas sus apps con bandera en off). Una
    // app sí tiene pantalla propia, así que sobrevive sin menús si se otorgó entera.
    const whole = inLicense.full.has(node.key) && inRole.full.has(node.key);
    if (node.kind !== "category" && whole) continue;
    if (node.kind === "category" && kids.length === 0) continue;

    // El motivo sale de los hijos que SÍ estaban en la licencia y el rol: si a
    // ésos los apagó la plataforma, decir "no está en tu plan" sería falso.
    const expected = kids.filter((k) => inLicense.granted.has(k) && inRole.granted.has(k));
    reasons[node.key] = emptyContainerReason((expected.length ? expected : kids).map((k) => reasons[k]));
  }

  const keys: EntitlementKey[] = [];
  for (const node of catalog) {
    if (reasons[node.key] === "granted") keys.push(node.key);
  }

  return { keys: keys.sort(), reasons, unknownKeys: [...unknown].sort() };
}
